import { formatDate, formatTime, RESULT_EFFECTS } from '@erp/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Lock, MessageCircle, Pencil, RotateCcw, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';

import { api, errorMessage } from '../../lib/api.ts';
import {
  dayLabel,
  refreshActivities,
  useActivityTypes,
  zonedParts,
  type ActivityItem,
  type NextActivitySuggestion,
} from '../../lib/activities-api.ts';
import { useContact, whatsappLink } from '../../lib/contacts-api.ts';
import type { PropertyDetail } from '../../lib/properties-api.ts';
import { useCan, useSession } from '../../lib/session.ts';
import { useToast } from '../../lib/toast.tsx';
import { Alert } from '../ui/alert.tsx';
import { Badge } from '../ui/badge.tsx';
import { Button, buttonVariants } from '../ui/button.tsx';
import { Dialog } from '../ui/dialog.tsx';
import { Field, Select } from '../ui/field.tsx';
import { Loading } from '../ui/skeleton.tsx';
import { ActivityDialog, type ActivityDraft } from './activity-dialog.tsx';
import { TypeIcon } from './type-style.tsx';

const textarea = 'w-full rounded-lg border border-border bg-surface p-3 text-base sm:text-sm';

/** "Hoy 10:00–10:30" / "vie 02/10 · todo el día". */
export function whenLabel(a: Pick<ActivityItem, 'startsAt' | 'endsAt' | 'allDay'>, tz: string) {
  const day = formatDate(a.startsAt, tz);
  const today = formatDate(new Date(), tz);
  const prefix = day === today ? 'Hoy' : day;
  if (a.allDay) return `${prefix} · todo el día`;
  return `${prefix} ${formatTime(a.startsAt, tz)}${a.endsAt ? `–${formatTime(a.endsAt, tz)}` : ''}`;
}

/** Activities with their actions; the dialogs (edit, complete, next) live here. */
export function ActivityList({
  items,
  empty,
  showAssignee = false,
}: {
  items: ActivityItem[];
  empty: string;
  showAssignee?: boolean;
}) {
  const [dialog, setDialog] = useState<
    | { kind: 'edit' | 'complete' | 'delete' | 'whatsapp'; activity: ActivityItem }
    | { kind: 'next'; draft: ActivityDraft }
    | null
  >(null);
  const close = () => {
    setDialog(null);
  };
  // The dialogs stay mounted when the list empties (e.g. completing the last one).
  return (
    <>
      {items.length === 0 ? (
        empty && <p className="text-sm text-muted-foreground">{empty}</p>
      ) : (
        <ul className="divide-y divide-border">
          {items.map((a) => (
            <ActivityRow
              key={a.id}
              activity={a}
              showAssignee={showAssignee}
              onAction={(kind) => {
                setDialog({ kind, activity: a });
              }}
            />
          ))}
        </ul>
      )}
      {dialog?.kind === 'edit' && <ActivityDialog activity={dialog.activity} onClose={close} />}
      {dialog?.kind === 'complete' && (
        <CompleteDialog
          activity={dialog.activity}
          onClose={close}
          onNext={(draft) => {
            setDialog({ kind: 'next', draft });
          }}
        />
      )}
      {dialog?.kind === 'next' && <ActivityDialog draft={dialog.draft} onClose={close} />}
      {dialog?.kind === 'delete' && <DeleteDialog activity={dialog.activity} onClose={close} />}
      {dialog?.kind === 'whatsapp' && (
        <WhatsAppConfirmDialog activity={dialog.activity} onClose={close} />
      )}
    </>
  );
}

function ActivityRow({
  activity: a,
  showAssignee,
  onAction,
}: {
  activity: ActivityItem;
  showAssignee: boolean;
  onAction: (kind: 'edit' | 'complete' | 'delete' | 'whatsapp') => void;
}) {
  const { data: session } = useSession();
  const tz = session?.tenant.timezone ?? 'America/El_Salvador';
  const client = useQueryClient();
  const notify = useToast();
  const canDelete = useCan('records.delete') && a.can.delete;
  const reopen = async () => {
    try {
      await api(`/activities/${a.id}/reopen`, { method: 'POST' });
      await refreshActivities(client);
    } catch (error) {
      notify(errorMessage(error), 'error');
    }
  };
  const confirmable =
    a.status === 'pending' && a.type.kind === 'visit' && a.contact?.visible === true;
  return (
    <li className="flex gap-3 py-3">
      <TypeIcon kind={a.type.kind} color={a.type.color} />
      <div className="min-w-0 flex-1 space-y-1">
        <p className="flex flex-wrap items-center gap-1.5">
          <span
            className={a.status === 'done' ? 'text-muted-foreground line-through' : 'font-medium'}
          >
            {a.title}
          </span>
          {a.isPrivate && (
            <Badge>
              <Lock className="mr-1 size-3" aria-hidden="true" /> Privada
            </Badge>
          )}
          {a.overdue && <Badge tone="danger">Vencida</Badge>}
          {a.result && <Badge tone="success">{a.result.name}</Badge>}
        </p>
        <p className="text-sm text-muted-foreground">
          {a.type.name} · {whenLabel(a, tz)}
          {showAssignee && ` · ${a.assignedTo.name}`}
        </p>
        {(a.deal ?? a.contact ?? a.property) && (
          <p className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
            {a.deal &&
              (a.deal.visible ? (
                <Link
                  to={`/negocios/${a.deal.id}`}
                  className="inline-flex min-h-6 items-center text-primary hover:underline"
                >
                  {a.deal.title}
                </Link>
              ) : (
                <span>{a.deal.title}</span>
              ))}
            {a.contact?.visible && a.contact.name && (
              <Link
                to={`/contactos/${a.contact.id}`}
                className="inline-flex min-h-6 items-center text-primary hover:underline"
              >
                {a.contact.name}
              </Link>
            )}
            {a.property &&
              (a.property.visible ? (
                <Link
                  to={`/propiedades/${a.property.id}`}
                  className="inline-flex min-h-6 items-center text-primary hover:underline"
                >
                  {a.property.code}
                </Link>
              ) : (
                <span>{a.property.code}</span>
              ))}
          </p>
        )}
        {a.note && <p className="text-sm whitespace-pre-line">{a.note}</p>}
        {a.resultComment && (
          <p className="text-sm whitespace-pre-line text-muted-foreground">{a.resultComment}</p>
        )}
        {a.can.edit && (
          <div className="-ml-3 flex flex-wrap gap-1 pt-1">
            {a.status === 'pending' ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  onAction('complete');
                }}
              >
                <Check /> Completar
              </Button>
            ) : (
              <Button size="sm" variant="ghost" onClick={() => void reopen()}>
                <RotateCcw /> Marcar pendiente
              </Button>
            )}
            {confirmable && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  onAction('whatsapp');
                }}
              >
                <MessageCircle /> Confirmar visita
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              aria-label={`Editar ${a.title}`}
              onClick={() => {
                onAction('edit');
              }}
            >
              <Pencil /> Editar
            </Button>
            {canDelete && (
              <Button
                size="sm"
                variant="ghost"
                aria-label={`Eliminar ${a.title}`}
                onClick={() => {
                  onAction('delete');
                }}
              >
                <Trash2 />
              </Button>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

/**
 * HU-AGE-06/07: done with a result of its type. A visit result marks the property in
 * the deal; then it offers to schedule what comes next.
 */
export function CompleteDialog({
  activity,
  onClose,
  onNext,
}: {
  activity: ActivityItem;
  onClose: () => void;
  onNext: (draft: ActivityDraft) => void;
}) {
  const types = useActivityTypes();
  const client = useQueryClient();
  const notify = useToast();
  const [resultId, setResultId] = useState('');
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [suggestion, setSuggestion] = useState<NextActivitySuggestion | null>(null);
  const results = types.data?.find((t) => t.id === activity.type.id)?.results ?? [];
  const chosen = results.find((r) => r.id === resultId);

  const save = async () => {
    setSaving(true);
    try {
      const done = await api<{ suggestion: NextActivitySuggestion | null }>(
        `/activities/${activity.id}/complete`,
        { method: 'POST', body: { resultId: resultId || null, comment: comment.trim() || null } },
      );
      await refreshActivities(client);
      notify('Actividad completada.', 'success');
      if (done.suggestion) setSuggestion(done.suggestion);
      else onClose();
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (suggestion) {
    const draft: ActivityDraft = {
      typeId: suggestion.typeId,
      title: suggestion.title,
      deal:
        suggestion.dealId && activity.deal
          ? { id: activity.deal.id, label: activity.deal.title }
          : null,
      contact:
        suggestion.contactId && activity.contact
          ? { id: activity.contact.id, label: activity.contact.name ?? 'Contacto' }
          : null,
      property:
        suggestion.propertyId && activity.property
          ? {
              id: activity.property.id,
              label: `${activity.property.code} · ${activity.property.title}`,
            }
          : null,
    };
    return (
      <Dialog
        open
        onClose={onClose}
        title={
          suggestion.reason === 'second_visit'
            ? '¿Agendar la segunda visita?'
            : '¿Cuál es el siguiente paso?'
        }
        description={
          suggestion.reason === 'second_visit'
            ? 'El cliente quiere volver a ver la propiedad.'
            : 'El negocio se quedó sin actividades pendientes. Un negocio abierto siempre debería tener la próxima.'
        }
      >
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={onClose}>
            Ahora no
          </Button>
          <Button
            variant="brand"
            onClick={() => {
              onNext(draft);
            }}
          >
            Programar
          </Button>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={onClose} title="Completar actividad" description={activity.title}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        {results.length > 0 && (
          <Field
            label="Resultado"
            hint={
              chosen && chosen.effect !== 'none' && activity.deal && activity.property
                ? `${RESULT_EFFECTS[chosen.effect]} en el negocio.`
                : undefined
            }
          >
            {(a11y) => (
              <Select
                {...a11y}
                value={resultId}
                onChange={(e) => {
                  setResultId(e.target.value);
                }}
              >
                <option value="">Sin resultado</option>
                {results.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
        <Field label="Comentario">
          {(a11y) => (
            <textarea
              {...a11y}
              rows={3}
              maxLength={1000}
              className={textarea}
              value={comment}
              onChange={(e) => {
                setComment(e.target.value);
              }}
            />
          )}
        </Field>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" variant="brand" loading={saving}>
            Completar
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function DeleteDialog({ activity, onClose }: { activity: ActivityItem; onClose: () => void }) {
  const client = useQueryClient();
  const notify = useToast();
  const [saving, setSaving] = useState(false);
  return (
    <Dialog
      open
      onClose={onClose}
      title="Eliminar actividad"
      description={`“${activity.title}” pasa a la papelera; se puede restaurar durante 30 días.`}
    >
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button variant="secondary" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          variant="danger"
          loading={saving}
          onClick={() => {
            setSaving(true);
            void api(`/activities/${activity.id}`, { method: 'DELETE' })
              .then(async () => {
                await refreshActivities(client);
                notify('Actividad eliminada.', 'success');
                onClose();
              })
              .catch((error: unknown) => {
                notify(errorMessage(error), 'error');
                setSaving(false);
              });
          }}
        >
          Eliminar
        </Button>
      </div>
    </Dialog>
  );
}

/**
 * Confirms a visit by WhatsApp: day, time, property code and ZONE only. Never the
 * exact address: the message leaves the system (and may be forwarded).
 */
function WhatsAppConfirmDialog({
  activity,
  onClose,
}: {
  activity: ActivityItem;
  onClose: () => void;
}) {
  const { data: session } = useSession();
  const tz = session?.tenant.timezone ?? 'America/El_Salvador';
  const contact = useContact(activity.contact?.id ?? '', Boolean(activity.contact));
  const propertyId = activity.property?.visible ? activity.property.id : null;
  const property = useQuery({
    queryKey: ['properties', 'detail', propertyId],
    queryFn: () => api<PropertyDetail>(`/properties/${propertyId ?? ''}`),
    enabled: propertyId !== null,
    retry: false,
  });
  const [edited, setEdited] = useState<string | null>(null);

  const loading = contact.isPending || (propertyId !== null && property.isPending);
  const failed = contact.error ?? property.error;
  let draft = '';
  let phone: string | undefined;
  if (contact.data) {
    phone = (contact.data.phones.find((p) => p.whatsapp) ?? contact.data.phones[0])?.number;
    const { date, time } = zonedParts(activity.startsAt, tz);
    const where = property.data
      ? ` a la propiedad ${property.data.code} en ${property.data.district.name}, ${property.data.district.municipality}`
      : activity.property
        ? ` a la propiedad ${activity.property.code}`
        : '';
    draft =
      `Hola ${contact.data.name.split(' ')[0] ?? ''}, te confirmo la visita del ${dayLabel(date, true)}${
        activity.allDay ? '' : ` a las ${time}`
      }${where}. Cualquier cambio me avisas. ${session?.user.fullName ?? ''}`.trim();
  }
  const text = edited ?? draft;

  return (
    <Dialog
      open
      onClose={onClose}
      title="Confirmar la visita por WhatsApp"
      description="El mensaje lleva la zona, nunca la dirección exacta: se la das en persona o por llamada."
    >
      {failed ? (
        <Alert tone="danger">{errorMessage(failed)}</Alert>
      ) : loading ? (
        <Loading />
      ) : (
        <div className="space-y-4">
          <Field label="Mensaje">
            {(a11y) => (
              <textarea
                {...a11y}
                rows={4}
                className={textarea}
                value={text}
                onChange={(e) => {
                  setEdited(e.target.value);
                }}
              />
            )}
          </Field>
          {phone ? (
            <a
              href={whatsappLink(phone, text)}
              target="_blank"
              rel="noopener noreferrer"
              className={buttonVariants({ variant: 'brand' })}
              onClick={onClose}
            >
              <MessageCircle /> Abrir WhatsApp
            </a>
          ) : (
            <Alert tone="warning">El contacto no tiene teléfono.</Alert>
          )}
        </div>
      )}
    </Dialog>
  );
}
